<template>
  <div class="campaign-performance-report relative text-lg mb-2 flex flex-col justify-center items-center">
    <report-header
      :error.sync="error"
      :range="range"
      :report="report"
      @getReport="getReport"
    >
      <template #title>
        campaign performance report
      </template>
      <template #subtitle>
        compare and analyze the performance of your campaigns
      </template>
    </report-header>
    <div v-if="loadingReport" class="flex justify-center align-center mt-12">
      <loading-icon />
    </div>
    <div v-else-if="report" class="mt-4 lg:-ml-20 w-full">
      <sb-custom-table
        :rows="tableRows"
        :columns="tableColumns"
        :defaultSort="sortDirection"
        :defaultSortColumn="sortColumn"
        @sortChange="updateTableSort"
        disableTableSort
        sortable
        class="custom-table"
      >
        <template #link="item">
          <a :href="'/campaigns/'+item.row['campaign_id']">
            +
          </a>
        </template>
        <template #selected_groups="item">
          <popper
            v-if="item.row.selected_groups.substr(-3) === '...'"
            trigger="hover"
            :options="{ placement: 'top' }"
            :delay-on-mouse-over="300"
          >
            <div class="popper">
              <strong>groups selected</strong>
              <div v-for="group in report.output_data.results.find(row => row.id === item.row.id).selected_groups">
                <strong class="mb-2">{{ group.split('-')[0] }}</strong>
                {{ group.substr( group.split('-')[0].length ) }}
              </div>

            </div>
            <div slot="reference">
              {{ item.row.selected_groups }}
            </div>
          </popper>
          <template v-else>{{ item.row.selected_groups }}</template>
        </template>
      </sb-custom-table>
      <div v-if="pagesTotal > 1" class="flex justify-center items-center mt-10">
        <pagination
          :page.sync="page"
          :pages="pagesTotal"
          :gap="2"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { subDays } from 'date-fns';
import { utcToZonedTime } from "date-fns-tz";
import { mapGetters } from "vuex";
import { SbCustomTable, Pagination, LoadingIcon } from '@springbig/sb-components';
import { reportsAPI } from '../../services/api/reportsAPI';
import { commafyNumber, truncateWithEllipses } from "../../utilities";
import ReportHeader from "./ReportHeader";
import Popper from 'vue-popperjs';
import { uniqueId } from "lodash";

export default {
  name: 'CampaignPerformanceReport',
  components: {
    SbCustomTable,
    LoadingIcon,
    Pagination,
    ReportHeader,
    Popper
  },
  data() {
    return {
      range: null,
      page: 0,
      sortColumn: "sent_at",
      sortDirection: "desc",
      report: null,
      tableRows: [],
      loadingReport: false,
      tableColumns: [
        { key: "link", value: "", sortable: false, width:20 },
        { key: "sent_at", value: "date of campaign" },
        { key: "selected_groups", value: "groups selected", sortable: false },
        { key: "sms_count", value: "sms recipients" },
        { key: "email_count", value: "email recipients" },
        { key: "push_count", value: "app recipients" },
        { key: "credits_used", value: "credits used" },
        { key: "campaign_cost", value: "cost of campaign" },
        { key: "click_count", value: "total clicks" },
        { key: "visit_count", value: "visits" },
        { key: "total_spend", value: "spend" },
        { key: "spend_recipient_average", value: "spend/recipient" },
      ],
      error: {},
    }
  },
  async created() {
    await this.$store.dispatch('LOAD_CURRENT_MERCHANT');
    this.range = this.defaultDateRange();
  },
  watch: {
    page() {
      this.getReport();
    }
  },
  computed: {
    pagesTotal(){
      return this.report?.pagination?.total_pages || 1;
    },
    ...mapGetters(['merchantTimeZone'])
  },
  methods: {
    defaultDateRange() {
      const currentDate = utcToZonedTime(new Date(), this.merchantTimeZone);
      const minimumDate = subDays(currentDate, 90);

      return {
        min: minimumDate,
        start: minimumDate,
        max: subDays(currentDate, 1),
        end: subDays(currentDate, 1)
      }
    },
    formatReportData() {
      const results = this.report.output_data.results;
      if (results.length > 0) {
        this.tableRows = results.map(row => {
          let clickCount = 0
          return {
            ...row,
            selected_groups: truncateWithEllipses(row.selected_groups.join('; '), 68),
            sent_at: row.sent_at,
            sms_count: commafyNumber(row.sms_count),
            email_count: commafyNumber(row.email_count),
            push_count: commafyNumber(row.push_count),
            credits_used: commafyNumber(row.credits_used),
            campaign_cost: `$${commafyNumber(Math.round(row.campaign_cost*100)/100)}`,
            click_count: commafyNumber(row.click_count),
            visit_count: commafyNumber(row.visit_count),
            total_spend: `$${commafyNumber(Math.round(row.total_spend))}`,
            spend_recipient_average: `$${commafyNumber(Math.round(row.spend_recipient_average))}`,
          }
        });
      }else{
        this.tableRows = [];
      }
    },
    createNewReport() {
      return reportsAPI.createReport('Reports::CampaignPerformanceReport', this.range.start, this.range.end);
    },
    fetchExistingReport() {
      return reportsAPI.getReportById(this.report.id, this.page + 1, {columnKey: this.sortColumn, direction: this.sortDirection});
    },
    updateTableSort(sort) {
      if (
        ( this.sortColumn !== sort.column
          || this.sortDirection !== sort.sorting )
        && this.tableRows.length > 0
      ) {
        this.sortColumn = sort.column;
        this.sortDirection = sort.sorting;
        this.page = 0
        this.getReport();
      }
    },
    async getReport(newReport) {
      this.loadingReport = true;
      try {
        const { report, pagination } = !this.report?.id || (this.report.id && newReport)
          ? await this.createNewReport()
          : await this.fetchExistingReport();
        report.output_data.results = report.output_data.results.map(row => ({...row, id:uniqueId()}))
        this.report = { pagination, ...report };
        this.formatReportData();
      } catch(error){
        this.error.message = "error, please refresh or contact support";
      } finally {
        this.loadingReport = false;
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@media only screen and (min-width: 1024px) {
  .custom-table {
    overflow-x: visible;
  }
}
div.popper {
  width: 300px;
  padding: 10px;
  box-shadow: none;
  border: 2px solid #128ff9;
  border-radius: 10px;
  line-height: 1.3rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  ::v-deep .popper__arrow {
    $arrowSize: 8px;
    border-width: $arrowSize $arrowSize 0 $arrowSize;
    border-color: #128ff9 transparent transparent transparent;
    bottom: -$arrowSize;
    left: calc(50% - #{$arrowSize});
  }
}
</style>
